import { BUSINESS_TYPE, BusinessTypeKey, IS_CLINIC, LABELS } from "../../platform/config";

export interface TourStep {
  /** Page to open first, relative to /dashboard/:businessId ("" = home). */
  path?: string;
  /** The `data-tour` value of the element to point at; none = a centred card. */
  target?: string;
  title: string;
  body: string;
}

export interface TourTopic {
  id: string;
  title: string;
  /** One line under the title in the help list. */
  summary: string;
  /** Part of the first-run setup checklist. */
  setup: boolean;
  steps: TourStep[];
}

const unit = LABELS.unit.toLowerCase();
const client = LABELS.client.toLowerCase();
const clients = LABELS.clients.toLowerCase();
const staffPlural = LABELS.staffPlural;

// What a typical service looks like for each kind of business.
const SERVICE_EXAMPLES: Record<BusinessTypeKey, { list: string; one: string }> = {
  NEUTRAL: { list: "Consultation, session, follow-up...", one: "Consultation, 30 min, $30" },
  CLINIC: { list: "Consultation, follow-up, cleaning...", one: "Consultation, 30 min, $40" },
  SPA: { list: "Massage, facial, body scrub...", one: "Deep tissue massage, 60 min, $50" },
  SALON: { list: "Haircut, colour, blow-dry, beard...", one: "Haircut, 30 min, $15" },
  BEAUTY: { list: "Gel nails, lash lift, brows...", one: "Gel manicure, 45 min, $25" },
};
const serviceExample = SERVICE_EXAMPLES[BUSINESS_TYPE];

/**
 * Everything the help center can show. Each step opens the page it is about
 * and points at the real button, so the owner learns where things live.
 * Shared by both builds; only the words differ.
 */
export const TOUR_TOPICS: TourTopic[] = [
  {
    id: "units",
    title: IS_CLINIC ? `Add your ${staffPlural}` : "Add your courts",
    summary: IS_CLINIC
      ? `Each ${LABELS.staff} (or room) gets their own calendar.`
      : "Each court gets its own calendar.",
    setup: true,
    steps: [
      {
        path: "/overview",
        target: "add-unit",
        title: IS_CLINIC ? `Add a ${unit} for each ${LABELS.staff}` : "Add a court",
        body: IS_CLINIC
          ? `Click "Add ${unit}" and type the ${LABELS.staff}'s name, for example "${LABELS.unitExample}". Each ${unit} is a column in your schedule; add one per ${LABELS.staff} or room.`
          : "Click here and give the court a name. Each court is a column in your schedule.",
      },
    ],
  },
  {
    id: "hours",
    title: "Set opening hours",
    summary: `When each ${unit} can be booked, and the slot length.`,
    setup: true,
    steps: [
      {
        path: "/settings/advanced",
        target: "opening-hours",
        title: "Opening hours",
        body: `Pick a ${unit}, set the opening and closing time and the slot length, then press Save. Repeat for each ${unit}.`,
      },
    ],
  },
  {
    id: "services",
    title: "Add your services",
    summary: IS_CLINIC
      ? `${serviceExample.list} with duration and price.`
      : "What can be booked, with duration and price.",
    setup: true,
    steps: [
      {
        path: "/settings/services",
        target: "add-service",
        title: "Add a service",
        body: IS_CLINIC
          ? `Click here, choose the ${unit}, and set the name, duration and price (for example "${serviceExample.one}"). ${LABELS.clients} pick from these when they book.`
          : "Click here, choose the court, and set the name, duration and price.",
      },
    ],
  },
  {
    id: "consent",
    title: IS_CLINIC ? `Consent forms and ${LABELS.medicalInfo.toLowerCase()}` : "Consent forms and health lists",
    summary: `What ${clients} sign, and the allergy and condition lists.`,
    setup: true,
    steps: [
      {
        path: "/settings/consent-forms",
        target: "new-consent-form",
        title: "Write a consent form",
        body: `Add the texts ${clients} sign (treatment consent, data privacy, photos...). They sign on screen with a finger or the mouse.`,
      },
      {
        path: "/settings/consent-forms",
        target: "record-lists",
        title: "Your allergy and condition lists",
        body: `These are suggested while filling in a ${client}'s ${LABELS.medicalInfo.toLowerCase()}. Add or remove items, then press Save lists.`,
      },
    ],
  },
  {
    id: "clients",
    title: `Add your first ${client}`,
    summary: `Name and phone, plus optional ${LABELS.medicalInfo.toLowerCase()} and consent.`,
    setup: true,
    steps: [
      {
        path: "/clients/clients",
        target: "add-client",
        title: `Add a ${client}`,
        body: `Enter the name and phone number. To fill in their ${LABELS.medicalInfo.toLowerCase()} and have them sign the consent forms, press the button at the bottom of the form; it opens right after saving.`,
      },
      {
        path: "/clients/clients",
        target: "client-list",
        title: `Open a ${client}'s file`,
        body: `Click any row to open the ${client}: their personal information first, then the ${LABELS.clientFile} tab with ${LABELS.medicalInfo.toLowerCase()}, upcoming and past ${LABELS.visits.toLowerCase()}, a note per ${LABELS.visit.toLowerCase()}, files and consent forms.`,
      },
    ],
  },
  {
    id: "booking",
    title: IS_CLINIC ? "Book an appointment" : "Make a booking",
    summary: IS_CLINIC ? "From anywhere, with the New appointment button." : "Straight on the calendar.",
    setup: true,
    steps: IS_CLINIC
      ? [
          {
            path: "",
            target: "new-appointment",
            title: "New appointment",
            body: `This button is always here at the top. Pick the ${client}, the ${unit} and the service, then a free time.`,
          },
        ]
      : [
          {
            path: "/appointment",
            title: "Book on the calendar",
            body: "Click an empty slot on a court's column to create a booking there.",
          },
        ],
  },
  // Only self-serve organizations pay a subscription.
  ...(IS_CLINIC
    ? [
        {
          id: "subscription",
          title: "Your subscription",
          summary: "Your plan, paying with Whish, and invoices.",
          setup: false,
          steps: [
            {
              path: "/subscription",
              target: "packages",
              title: "Pay for a package",
              body: "Choose a package and pay with Whish. Nothing renews by itself; you get a reminder 3 days before the end, and your invoices are listed below.",
            },
          ],
        },
      ]
    : []),
  {
    id: "help",
    title: "Find this help again",
    summary: "The Help button at the bottom of the menu.",
    setup: false,
    steps: [
      {
        target: "help-row",
        title: "Help is always here",
        body: "Open it any time to see how to do something; each topic takes you to the right page and shows you the button.",
      },
    ],
  },
];

export const SETUP_TOPICS = TOUR_TOPICS.filter((topic) => topic.setup);
